/**
 * Utility functions for expanding template keys into multiple widgets
 * This allows a single API template to be rendered as multiple widgets in the UI
 */

export interface ExpandableTemplate {
  productType: string;
  displayName?: string;
  [key: string]: any;
}

/**
 * Configuration for templates that should be expanded into multiple widgets
 * Maps a template key to an array of expanded template keys with their display names
 */
const TEMPLATE_EXPANSION_CONFIG: Record<string, Array<{ key: string; displayName: string }>> = {
  'DASHBOARD.MY_RECOMMENDATIONS': [
    {
      key: 'DASHBOARD.MY_RECOMMENDATIONS.RECOMMENDATION_TOP_TIPS',
      displayName: 'Top Tips'
    },
    {
      key: 'DASHBOARD.MY_RECOMMENDATIONS.RECOMMENDATION_TOP_PROGRAMS',
      displayName: 'Top Programs'
    },
    {
      key: 'DASHBOARD.MY_RECOMMENDATIONS.RECOMMENDATION_TIPS_LIBRARY',
      displayName: 'Tips Library'
    }
  ]
};

/**
 * Expands a template into multiple templates if it's in the expansion config
 * @param template - The template to potentially expand
 * @returns Array of templates (expanded or original)
 */
export function expandTemplate(template: ExpandableTemplate): ExpandableTemplate[] {
  const { productType, ...rest } = template;
  
  // Check if this template should be expanded
  if (!TEMPLATE_EXPANSION_CONFIG[productType]) {
    // Return original template as-is
    return [template];
  }
  
  // Expand into multiple templates
  const expansionConfig = TEMPLATE_EXPANSION_CONFIG[productType];
  return expansionConfig.map(config => ({
    ...rest,
    productType: config.key,
    displayName: config.displayName || rest.displayName
  }));
}

/**
 * Expands an array of templates
 * @param templates - Array of templates to potentially expand
 * @returns Flattened array of expanded templates
 */
export function expandTemplates(templates: ExpandableTemplate[]): ExpandableTemplate[] {
  return templates.flatMap(template => expandTemplate(template));
}

